/**
 * DB 콘텐츠(공지 `notice` · FAQ `faq` · 타깃 공지 `announcement` · 작품명 `licensed_video`)의 **표시용** 번역.
 * 기획: `docs/ops/app-i18n-plan.md` §18
 *
 * 코드 문구는 `tt()`, 데이터 라벨은 `ttl()`, 서버가 만든 문장은 `st()` — 그리고 **운영팀이 쓴 글**은 여기다.
 * 원문은 원래 표에 한국어로 그대로 두고, 언어별 번역은 옆 표 `content_translation` 에 쌓인다
 * (어드민·영상권리사 화면은 계속 한국어 원문을 본다).
 *
 * 세 가지 규칙
 *   1. 번역이 없으면 **한국어 원문** — 화면이 비지 않는다.
 *   2. 원문이 번역 뒤에 바뀌었으면(`is_stale`) 낡은 번역 대신 **한국어 원문**을 쓴다.
 *      틀린 안내를 보여 주느니 원문이 낫다.
 *   3. 조회가 실패해도 화면은 그대로 뜬다 — 번역은 덤이지 전제가 아니다.
 */
import { supabase } from "./supabaseClient";
import i18n from "../i18n";

// 작품명(`licensed_video`)은 `WorkTitleContext` 가 읽는다(§19 — 공식 현지 제목만). 2026-09-29 전까지 이 목록에 없어
// 작품명 번역(영어 178건)이 한 번도 화면에 나오지 않았다.
export const TRANSLATABLE_TABLES = ["notice", "faq", "announcement", "licensed_video"];

/** 지금 언어가 번역 대상인가 (한국어면 조회 자체를 하지 않는다). */
export const needsContentTranslation = (lang = i18n.language) => ["en", "ja", "zh"].includes(lang);

/**
 * 화면 한 곳이 쓸 번역을 한 번에 읽는다.
 * @returns Map<`${source_id}|${field}`, string> — 낡은 번역은 담지 않는다
 */
export async function fetchContentTranslations(table, ids = null, lang = i18n.language) {
  const map = new Map();
  if (!TRANSLATABLE_TABLES.includes(table) || !needsContentTranslation(lang)) return map;
  try {
    const { data, error } = await supabase.rpc("get_content_translations", {
      p_table: table,
      p_lang: lang,
      p_ids: ids && ids.length ? ids.map(String) : null,
    });
    // 표·함수가 아직 없거나(PGRST202 — 마이그레이션 전) 오류여도 화면은 한국어로 그대로 뜬다
    if (error || !Array.isArray(data)) return map;
    for (const row of data) {
      if (row.is_stale) continue; // 원문이 바뀐 뒤의 낡은 번역은 쓰지 않는다
      map.set(`${row.source_id}|${row.field}`, row.value);
    }
  } catch (e) {
    /* 네트워크 실패도 마찬가지 — 조용히 한국어 */
  }
  return map;
}

/** 번역이 있으면 번역, 없으면 한국어 원문. */
export function pickContentText(map, id, field, koText) {
  if (!map || !id) return koText;
  return map.get(`${String(id)}|${field}`) || koText;
}
